import { computePosition } from '@floating-ui/dom'
import { SuggestionData } from 'ckeditor5-premium-features'
import { SuggestionDataMap } from 'components/WorkspaceEditor/SuggestionStore'
import { TrackChangeType, TrackedChange } from 'models/changes'
import { TRACK_CHANGE_STATUS, TRACK_CHANGE_STATUS_VALUES } from '../constants'

/**
 * Options for setUpdatedCardPositions to reduce parameter count.
 */
export interface SetUpdatedCardPositionsOptions {
    flatSuggestionsRef: React.MutableRefObject<SuggestionData[]>
    prevCardBottomRef: React.MutableRefObject<number>
    rafRef: React.MutableRefObject<number | null>
    overrideSuggestions?: Record<string, SuggestionDataMap>
}

const TIME_FORMATTERS = {
    TODAY_TIME_FORMATTER: new Intl.DateTimeFormat('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
    }),
    DATE_TIME_FORMATTER: new Intl.DateTimeFormat('en-US', {
        year: 'numeric',
        month: 'numeric',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
    }),
}

/**
 * A utility function to build an array of suggestions from tracked changes.
 * It maps each tracked change to a suggestion data object using the provided function.
 * @param changes
 * @param createSuggestionDataFromTrackChange
 * @param from
 * @returns TrackedChange[]
 */
export const buildSuggestionArray = (
    changes: TrackedChange[],
    toSuggestionData: (value: TrackedChange, from?: string) => SuggestionData,
    from?: string
) => {
    return changes.reduce<
        {
            id: string
            createdAt: number
            suggestion: SuggestionData
        }[]
    >((arr, value) => {
        const suggestionData = toSuggestionData(value, from)
        if (!suggestionData.id) return arr

        arr.push({
            id: suggestionData.id,
            createdAt: suggestionData.createdAt?.getTime?.() ?? 0,
            suggestion: suggestionData,
        })
        return arr
    }, [])
}

/**
 *  Sorts a suggestion array by `createdAt` in descending order.
 * @param suggestionArray
 * @returns Ordered array of suggestions
 */
export const sortSuggestionArray = (
    suggestionArray: {
        id: string
        createdAt: number
        suggestion: SuggestionData
    }[]
) => {
    return suggestionArray.slice().sort((a, b) => b.createdAt - a.createdAt)
}

/**
 * Converts a sorted array into a record of SuggestionDataMap.
 * Each entry in the record contains the suggestion ID and an array of suggestions.
 * @param sortedArray - The sorted array of suggestions.
 * @returns A record where each key is a suggestion ID and the value is an object containing the ID and an array of suggestions.
 */
export const buildSuggestionRecord = (
    sortedArray: { id: string; suggestion: SuggestionData }[]
): Record<string, SuggestionDataMap> => {
    const record: Record<string, SuggestionDataMap> = {}
    for (const item of sortedArray) {
        record[item.id] = {
            id: item.id,
            suggestions: [item.suggestion],
        }
    }
    return record
}

/** Normalizes tracked changes from the server and identifies stale IDs to remove. */
export const normalizeTrackChanges = (
    incoming: TrackedChange[],
    existing: Record<string, TrackedChange>
): {
    trackChangesDataWithId: Record<string, TrackedChange>
    removeTrackChangesId: string[]
} => {
    if (!incoming || !Array.isArray(incoming) || incoming.length === 0) {
        return { trackChangesDataWithId: {}, removeTrackChangesId: [] }
    }
    const trackChangesDataWithId: Record<string, TrackedChange> = {}
    const removeTrackChangesId: string[] = []
    const newIdsByElement = new Map<string, string>()
    // Build a set of all new IDs and a map of elementId to newId
    for (const change of incoming) {
        const baseId = isModifiedOrReplaced(change)
            ? change.id
            : change.contentItemElementId
        const newId = `${baseId}:${change.status}`

        // skip unchanged
        if (existing[newId]?.status === change.status) continue

        trackChangesDataWithId[newId] = change
        newIdsByElement.set(baseId, newId)
    }

    // Build incoming element IDs set
    const incomingElementIds = new Set(
        incoming.flatMap((change) =>
            [change.id, change.contentItemElementId].filter(Boolean)
        )
    )

    for (const id of Object.keys(existing)) {
        const [elementId] = id.split(':')

        // Check if status changed
        const newId = newIdsByElement.get(elementId)
        if (newId && id !== newId) {
            removeTrackChangesId.push(id)
        }
        // Check if element no longer exists in incoming
        else if (!incomingElementIds.has(elementId)) {
            removeTrackChangesId.push(id)
        }
    }

    return { trackChangesDataWithId, removeTrackChangesId }
}

/**
 * Utility to cache today's date information to avoid repeated Date operations.
 */
const DateCache = (() => {
    let _cache: {
        date: string
        year: number
        month: number
        day: number
    } | null = null

    return {
        /**
         * Gets today's date information with caching to avoid repeated Date operations
         */
        getTodayInfo: () => {
            const now = new Date()
            const todayString = now.toDateString()

            // Use cache if it's still the same day
            if (_cache?.date === todayString) {
                // Return a copy to prevent external mutation
                return { ..._cache }
            }

            // Update cache
            _cache = {
                date: todayString,
                year: now.getFullYear(),
                month: now.getMonth(),
                day: now.getDate(),
            }

            return { ..._cache }
        },

        resetCache: () => {
            _cache = null
        },
    }
})()

/**
 * Resets the cached date information to ensure fresh calculations
 */
export const resetDateCache = () => {
    DateCache.resetCache()
}

/**
 * takes a timestamp string and formats it to show "Today, HH:MM:SS AM/PM" if the date is today.
 * @param timeStamp - The timestamp string to format
 * @returns Formatted timestamp string or empty string if invalid
 */
export const formatTimeStamp = (timeStamp: string): string => {
    if (!timeStamp?.trim()) return ''

    try {
        const date = new Date(timeStamp)

        if (isNaN(date.getTime())) return timeStamp

        const today = DateCache.getTodayInfo()

        const isToday =
            date.getFullYear() === today.year &&
            date.getMonth() === today.month &&
            date.getDate() === today.day

        const formattedDate = isToday
            ? `Today ${TIME_FORMATTERS.TODAY_TIME_FORMATTER.format(date)}`
            : TIME_FORMATTERS.DATE_TIME_FORMATTER.format(date).replace(',', '')

        return formattedDate
    } catch {
        return typeof timeStamp === 'string'
            ? timeStamp.replace(/,/, '').replace(/:\d{2}\s/, ' ')
            : timeStamp
    }
}

/**
 * Retrieves the corresponding track change data for a suggestion,
 * based on filter criteria and available track changes.
 *
 * @param suggestion - The CKEditor suggestion data
 * @param trackChangesData - Map of track changes keyed by suggestion:status
 * @param isNotDefaultSuggestionCardFilter - Whether a custom filter is applied
 * @param suggestionCardFilter - The current filter value (OPEN/RESOLVED)
 * @returns The matched TrackedChange or null
 */
export function getTrackChangeById(
    suggestion: SuggestionData,
    trackChangesData: Record<string, TrackedChange>,
    isNotDefaultSuggestionCardFilter?: boolean,
    suggestionCardFilter?: string
): TrackedChange | null {
    // Default to PENDING if no custom filter or filter is OPEN
    if (
        !isNotDefaultSuggestionCardFilter ||
        suggestionCardFilter === TRACK_CHANGE_STATUS_VALUES.OPEN
    ) {
        let trackChange =
            trackChangesData[
                `${suggestion?.id}:${TRACK_CHANGE_STATUS.PENDING}`
            ] || null

        if (!trackChange) {
            trackChange =
                trackChangesData[
                    `${suggestion?.id}:${TRACK_CHANGE_STATUS.OPTIONGROUP}`
                ] || null
        }

        return trackChange
    }

    // If filter is RESOLVED, check APPROVED then REJECTED
    if (suggestionCardFilter === TRACK_CHANGE_STATUS_VALUES.RESOLVED) {
        return (
            trackChangesData[
                `${suggestion?.id}:${TRACK_CHANGE_STATUS.APPROVED}`
            ] ||
            trackChangesData[
                `${suggestion?.id}:${TRACK_CHANGE_STATUS.REJECTED}`
            ] ||
            null
        )
    }

    // Fallback: try REJECTED if not already checked
    return (
        trackChangesData[`${suggestion?.id}:${TRACK_CHANGE_STATUS.REJECTED}`] ||
        null
    )
}

/**
 * Updates the positions of suggestion cards based on their target elements.
 * It computes the new positions and applies them with animation.
 *
 * @param flatSuggestionsRef - Ref to the flat list of suggestions
 * @param prevCardBottomRef - Ref to track the bottom position of the last card
 * @param rafRef - Ref to store the requestAnimationFrame ID for cancellation
 * @param overrideSuggestions - Optional override for suggestions to use instead of the flat list
 * @returns A promise that resolves when the positions are updated
 */
export const setUpdatedCardPositions = async ({
    flatSuggestionsRef,
    prevCardBottomRef,
    rafRef,
    overrideSuggestions,
}: SetUpdatedCardPositionsOptions) => {
    prevCardBottomRef.current = 0

    const allSuggestions = overrideSuggestions
        ? Object.values(overrideSuggestions).flatMap(
              (g) => g?.suggestions || []
          )
        : flatSuggestionsRef.current
    const suggestionPositions: {
        id: string
        top: number
        card: HTMLElement
        target: HTMLElement
    }[] = []

    for (const suggestion of allSuggestions) {
        const id = suggestion?.id
        const target = document.querySelector(
            `[data-suggestion='content-suggestion-id-${id}']`
        ) as HTMLElement
        const card = document.querySelector(
            `[data-suggestion-id='${id}']`
        ) as HTMLElement
        if (!target || !card) {
            if (card) {
                card.classList.remove('visible')
                card.style.top = '0px'
            }
            continue
        }

        const { y } = await computePosition(target, card)
        suggestionPositions.push({ id, top: y, card, target })
    }

    // Sort cards vertically
    suggestionPositions.sort((a, b) => a.top - b.top)
    const positions: { id: string; top: number }[] = []

    for (const { id, top, card } of suggestionPositions) {
        const cardHeight = card.offsetHeight
        let adjustedTop = top - 50
        if (adjustedTop < prevCardBottomRef.current) {
            adjustedTop = prevCardBottomRef.current + 8
        }
        prevCardBottomRef.current = adjustedTop + cardHeight
        positions.push({ id, top: adjustedTop })
    }

    // Cancel pending animation
    if (rafRef.current) {
        cancelAnimationFrame(rafRef.current)
        rafRef.current = null
    }

    // Animate position updates
    rafRef.current = requestAnimationFrame(() => {
        positions.forEach(({ id, top }) => {
            const el = document.querySelector(
                `[data-suggestion-id='${id}']`
            ) as HTMLElement
            if (el) {
                el.style.top = `${top}px`
                el.classList.add('visible')
            }
        })
    })
}

/**
 * Utility to check if a tracked change is MODIFIED or REPLACED.
 * @param trackChange - The tracked change object.
 * @returns true if changeType is MODIFIED or REPLACED, false otherwise.
 */
export const isModifiedOrReplaced = (trackChange?: TrackedChange): boolean => {
    if (!trackChange) return false

    return (
        trackChange?.changeType === TrackChangeType.MODIFIED ||
        trackChange?.changeType === TrackChangeType.REPLACED
    )
}
